'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LotCarsData } from '~/contracts/dealership';
import {
  getInertDots,
  getMakeChips,
  getMapDots,
  getRailCars,
  getSelectedCar,
  type MakeFilter
} from '~/utilities/dealership/lotMap';

const MOBILE_QUERY = '(max-width: 920px)';

// Single source of truth for the lot map: the active make filter and selected
// car drive the dots, the desktop detail card, and the mobile rail. On mobile,
// changing the selection pans the zoomed stage to center the car. The fleet is
// fetched server-side and passed in, so this hook stays purely about selection.
export default function useLotMapSelection({ cars, coords }: LotCarsData) {
  const [make, setMake] = useState<MakeFilter>('All');
  // Empty until the user picks a car; the default selection is derived from the
  // loaded fleet so it stays valid when live data replaces the seed snapshot.
  const [selectedVon, setSelectedVon] = useState('');

  const wrapRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const fromRailRef = useRef(false);

  const defaultVon = useMemo(
    () => getRailCars(cars, coords, 'All', '')[0]?.von ?? '',
    [cars, coords]
  );
  const activeVon = selectedVon || defaultVon;

  const makeChips = useMemo(() => getMakeChips(cars), [cars]);
  const mapDots = useMemo(
    () => getMapDots(cars, coords, make, activeVon),
    [activeVon, cars, coords, make]
  );
  const inertDots = useMemo(
    () => getInertDots(cars, coords, make),
    [cars, coords, make]
  );
  const railCars = useMemo(
    () => getRailCars(cars, coords, make, activeVon),
    [activeVon, cars, coords, make]
  );
  const selectedCar = useMemo(
    () => getSelectedCar(cars, activeVon),
    [activeVon, cars]
  );

  const isMobile = useCallback(
    () => window.matchMedia(MOBILE_QUERY).matches,
    []
  );

  const panMapToSelected = useCallback(
    (animate: boolean) => {
      const wrap = wrapRef.current;
      const stage = stageRef.current;
      if (!wrap || !stage) return;

      if (!isMobile()) {
        stage.style.transform = 'translate(-50%, -50%)';

        return;
      }

      const point = coords[activeVon];
      if (!point) return;

      const [percentX, percentY] = point;
      const maxX = Math.max(0, (stage.offsetWidth - wrap.clientWidth) / 2);
      const maxY = Math.max(0, (stage.offsetHeight - wrap.clientHeight) / 2);
      const offsetX = Math.max(
        -maxX,
        Math.min(maxX, stage.offsetWidth * (0.5 - percentX / 100))
      );
      const offsetY = Math.max(
        -maxY,
        Math.min(maxY, stage.offsetHeight * (0.5 - percentY / 100))
      );

      if (!animate) stage.style.transition = 'none';
      stage.style.transform = `translate(calc(-50% + ${offsetX.toFixed(
        1
      )}px), calc(-50% + ${offsetY.toFixed(1)}px))`;
      if (!animate) {
        // Force reflow so the transition reset takes effect immediately.
        void stage.offsetWidth;
        stage.style.transition = '';
      }
    },
    [activeVon, coords, isMobile]
  );

  const centerRailToSelected = useCallback(() => {
    const rail = railRef.current;
    if (!rail || !isMobile()) return;

    const card = rail.querySelector<HTMLElement>(`[data-von="${activeVon}"]`);
    if (card) {
      rail.scrollTo({
        behavior: 'smooth',
        left: Math.max(0, card.offsetLeft - 16)
      });
    }
  }, [activeVon, isMobile]);

  const syncFromRail = useCallback(() => {
    const rail = railRef.current;
    if (!rail || !isMobile()) return;

    const cards = Array.from(rail.querySelectorAll<HTMLElement>('[data-von]'));
    if (!cards.length) return;

    const anchor = rail.scrollLeft + 24;
    const first =
      cards.find(card => card.offsetLeft + card.offsetWidth * 0.5 >= anchor) ??
      cards[cards.length - 1];
    const von = first.dataset.von;

    if (von && von !== activeVon) {
      fromRailRef.current = true;
      setSelectedVon(von);
    }
  }, [activeVon, isMobile]);

  const selectCar = useCallback((von: string) => {
    setSelectedVon(von);
  }, []);

  // Pan the map and (unless the change came from the rail) re-center the rail.
  useEffect(() => {
    panMapToSelected(true);

    if (fromRailRef.current) {
      fromRailRef.current = false;
    } else {
      centerRailToSelected();
    }
  }, [activeVon, centerRailToSelected, panMapToSelected]);

  // Re-pan and re-center when the make filter changes.
  useEffect(() => {
    panMapToSelected(true);
    centerRailToSelected();
  }, [centerRailToSelected, make, panMapToSelected]);

  // Debounced rail-scroll auto-select.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;

    let timeoutId: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(syncFromRail, 80);
    };

    rail.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      rail.removeEventListener('scroll', onScroll);
      clearTimeout(timeoutId);
    };
  }, [syncFromRail]);

  // Keep the map centered on the selection across breakpoint/size changes.
  useEffect(() => {
    const onResize = () => panMapToSelected(false);
    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, [panMapToSelected]);

  return {
    hasCars: cars.length > 0,
    inertDots,
    make,
    makeChips,
    mapDots,
    railCars,
    railRef,
    selectCar,
    selectedCar,
    selectedVon: activeVon,
    setMake,
    stageRef,
    wrapRef
  };
}
